import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosInstance from 'axiosInstance';
import { subscribeMessageStream } from 'helpers/messages';

const defaultCounts = { stockBreached: 0, questionsUnanswered: 0, messagesUnread: 0, notificationsUnread: 0 };

const NavCountsContext = createContext(defaultCounts);

// Map subscription keys and topics to counter field names
const SUBSCRIPTION_KEY_TO_COUNTER_MAP = {
  'questions.new_question': 'questionsUnanswered',
  'sales.new_order': 'notificationsUnread',
  'sales.status_update': 'notificationsUnread',
  'claims.new': 'notificationsUnread',
  'claims.status_update': 'notificationsUnread',
};

export const NavCountsProvider = ({ children }) => {
  const [counts, setCounts] = useState(defaultCounts);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await axiosInstance.get('notifications/nav-counts');
      setCounts(response.data);
    } catch {
      // silent — badge counts are non-critical
    }
  }, []);

  // Handle real-time SSE events for immediate counter updates
  const handleConversationUpdated = useCallback(eventData => {
    const { system_notification, subscription_key, new_messages } = eventData || {};
    
    if (!system_notification || !subscription_key) {
      return;
    }

    const counterField = SUBSCRIPTION_KEY_TO_COUNTER_MAP[subscription_key];
    if (!counterField) {
      return;
    }

    setCounts(prevCounts => ({
      ...prevCounts,
      [counterField]: Math.max(0, (prevCounts[counterField] || 0) + (new_messages || 1))
    }));
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchCounts();
    
    // Poll every 60 seconds as fallback
    const pollId = setInterval(fetchCounts, 60000);

    // Subscribe to real-time SSE events for immediate updates
    let eventSource = null;
    try {
      eventSource = subscribeMessageStream({
        onConversationUpdated: handleConversationUpdated
      });
    } catch (error) {
      console.error('Error subscribing to message stream:', error);
    }

    return () => {
      clearInterval(pollId);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [fetchCounts, handleConversationUpdated]);

  return (
    <NavCountsContext.Provider value={counts}>
      {children}
    </NavCountsContext.Provider>
  );
};

export const useNavCounts = () => useContext(NavCountsContext);

export default NavCountsContext;
